import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Copy, CopyCheck, Send, MessageCircle, AtSign, Loader2 } from 'lucide-react';
import { useCreateShareableScore, shareUrl } from './api';
import { getMiniGame } from '@/features/games/_framework/registry';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Card, CardDescription } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { formatCompact } from '@/lib/utils';
/**
 * Modal de partage : crée un shareable_score à l'ouverture, affiche les
 * options de partage (Web Share API natif sur mobile, fallback clipboard +
 * deep links Twitter/WhatsApp/Telegram).
 */
export function ShareScoreModal({ open, onOpenChange, gameType, score, meta, sourceScoreId, }) {
    const create = useCreateShareableScore();
    const { toast } = useToast();
    const [shareId, setShareId] = useState(null);
    const [copied, setCopied] = useState(false);
    const def = getMiniGame(gameType);
    // Crée la ligne dès l'ouverture (idempotent côté UX : modal réouverte = nouveau partage).
    useEffect(() => {
        if (!open || shareId || create.isPending)
            return;
        void (async () => {
            try {
                const id = await create.mutateAsync({ gameType, score, meta, sourceScoreId });
                setShareId(id);
            }
            catch (err) {
                toast({ tone: 'error', title: 'Création impossible', description: err.message });
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Reset à la fermeture
    useEffect(() => {
        if (!open) {
            setShareId(null);
            setCopied(false);
        }
    }, [open]);
    const url = shareId ? shareUrl(shareId) : null;
    const text = `Mon score sur ${def?.name ?? gameType} aux Alfalympics : ${formatCompact(score)} pts. Bats-moi !`;
    async function copy() {
        if (!url)
            return;
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast({ tone: 'success', title: 'Lien copié' });
            setTimeout(() => setCopied(false), 2000);
        }
        catch {
            toast({ tone: 'error', title: 'Copie impossible' });
        }
    }
    async function nativeShare() {
        if (!url)
            return;
        try {
            if (navigator.share) {
                await navigator.share({ title: 'Les Alfalympics', text, url });
            }
            else {
                await copy();
            }
        }
        catch {
            /* user cancelled */
        }
    }
    function deepLink(target) {
        const u = encodeURIComponent(url ?? '');
        const t = encodeURIComponent(text);
        if (target === 'twitter')
            return `https://twitter.com/intent/tweet?text=${t}&url=${u}`;
        if (target === 'whatsapp')
            return `https://wa.me/?text=${t}%20${u}`;
        return `https://t.me/share/url?url=${u}&text=${t}`;
    }
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, title: "Partager ton score", description: "Lien public avec le d\u00E9tail de la partie. Tes amis peuvent tenter de te battre.", size: "md", footer: null, children: !shareId || create.isPending ? (_jsx("div", { className: "flex items-center justify-center py-8", children: _jsx(Loader2, { className: "h-6 w-6 animate-spin text-electric-300" }) })) : (_jsxs("div", { className: "space-y-4", children: [_jsxs(Card, { variant: "glow", padding: "md", className: "text-center", children: [_jsxs(Badge, { variant: "flame", size: "lg", className: "mb-2", children: [def?.emoji, " ", def?.name ?? gameType] }), _jsx("p", { className: "font-display text-5xl text-gradient-flame", children: formatCompact(score) }), _jsx("p", { className: "mt-1 text-xs uppercase tracking-wider text-ink-400", children: "points" })] }), _jsxs("div", { className: "rounded-2xl border border-ink-700/60 bg-ink-900/60 p-3", children: [_jsx("p", { className: "mb-2 text-2xs uppercase tracking-wider text-ink-400", children: "Lien public" }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "flex-1 truncate rounded-xl bg-ink-950/60 px-3 py-2 font-mono text-xs text-ink-200", children: url }), _jsx(Button, { size: "sm", variant: copied ? 'secondary' : 'primary', leftIcon: copied ? _jsx(CopyCheck, { className: "h-3.5 w-3.5" }) : _jsx(Copy, { className: "h-3.5 w-3.5" }), onClick: () => void copy(), children: copied ? 'Copié' : 'Copier' })] })] }), _jsxs("div", { className: "grid grid-cols-2 gap-2", children: [_jsx(Button, { variant: "primary", fullWidth: true, leftIcon: _jsx(Send, { className: "h-4 w-4" }), onClick: () => void nativeShare(), children: "Partager" }), _jsx("a", { href: deepLink('twitter'), target: "_blank", rel: "noopener noreferrer", className: "contents", children: _jsx(Button, { variant: "secondary", fullWidth: true, leftIcon: _jsx(AtSign, { className: "h-4 w-4" }), type: "button", children: "X / Twitter" }) }), _jsx("a", { href: deepLink('whatsapp'), target: "_blank", rel: "noopener noreferrer", className: "contents", children: _jsx(Button, { variant: "secondary", fullWidth: true, leftIcon: _jsx(MessageCircle, { className: "h-4 w-4" }), type: "button", children: "WhatsApp" }) }), _jsx("a", { href: deepLink('telegram'), target: "_blank", rel: "noopener noreferrer", className: "contents", children: _jsx(Button, { variant: "secondary", fullWidth: true, leftIcon: _jsx(Send, { className: "h-4 w-4" }), type: "button", children: "Telegram" }) })] }), _jsx(Card, { variant: "ghost", padding: "sm", className: "border border-ink-800", children: _jsxs(CardDescription, { className: "text-xs", children: [_jsx("strong", { className: "text-ink-200", children: "\u00C0 venir :" }), " image g\u00E9n\u00E9r\u00E9e dynamiquement (1080\u00D71080) pour les previews riches sur Twitter/Discord/Slack. Pour l'instant le lien ouvre une page web styl\u00E9e."] }) })] })) }));
}
